import { formatDateMMDDyyyy } from "../../util/DateUtils";
import Money from "js-money";
import { PartnerSubscriptionTransactionStatusTypeLabel, PartnerSubscriptionTypeLabel } from "../constants/securspace-constants";
import { DateFormats } from "../constants/securspace-constants";
import moment from "moment";

// Widened so the labels can be looked up with an arbitrary API status/type string
const transactionStatusLabels: Record<string, string> = PartnerSubscriptionTransactionStatusTypeLabel;
const subscriptionTypeLabels: Record<string, string> = PartnerSubscriptionTypeLabel;

/** Partner subscription as returned by the API; amount is in cents. */
export interface PartnerSubscriptionItem {
    id: string;
    type: string;
    creationDate: string;
    createdBy: string;
    reason: string;
    amount: number;
    paymentSource: string;
    active: boolean | null;
}

export interface FormattedSubscriptionItem {
    id: string;
    feeType: string;
    amount: string;
    createdOn: string;
    createdBy: string;
    paymentSource: string;
    active: string;
    reason: string;
}

export type SubscriptionCsvItem = Omit<FormattedSubscriptionItem, 'id'>;

/** Partner subscription invoice transaction as returned by the API; amount is in cents. */
export interface PartnerSubscriptionInvoiceItem {
    transactionNumber: string;
    amount: number;
    creationDate: string;
    serviceDate: string;
    status: string;
    type: string;
}

export interface FormattedInvoiceItem {
    type: string;
    amount: string;
    serviceMonth: string;
    invoiceNumber: string;
    paymentDate: string;
    status: string;
}

export const formatSubscriptionItem = (subscriptionItem: PartnerSubscriptionItem): FormattedSubscriptionItem => {

    const {id, type, creationDate, createdBy, reason, amount, paymentSource, active} = subscriptionItem;

    const formatAmount = (): string => {
        const money = new Money(amount, Money.USD);
        return `${Money.USD.symbol}${money.toString()}`;
    }

    return {
        id: id,
        feeType: type === "AGS_FEE" ? "AGS Fee" : type,
        amount: formatAmount(),
        createdOn: formatDateMMDDyyyy(creationDate),
        createdBy: createdBy,
        paymentSource: paymentSource,
        active: active !== null ? JSON.stringify(active) : "true",
        reason: reason,
    };

}

export const formatInvoiceItem = (invoiceItem: PartnerSubscriptionInvoiceItem): FormattedInvoiceItem => {
    const {transactionNumber, amount, creationDate, serviceDate, status, type} = invoiceItem;

    const formatAmount = (): string => {
        // If actual transaction amount is 0 then DataListResultsEntryRow.js will pass '' as the value
        let sanitizedValue: number;
        if (amount) {
            sanitizedValue = amount;
        } else {
           sanitizedValue = invoiceItem.amount ? invoiceItem.amount : 0;
        }
        const money = new Money(sanitizedValue, Money.USD);
        return `${Money.USD.symbol}${money.toString()}`;
    };

    const formatStatus = (): string => {
        return transactionStatusLabels[status] ? transactionStatusLabels[status] : status.replaceAll('_', ' ');
    }

    const formatDate = (): string => {
        return serviceDate ? moment(new Date(serviceDate)).format(DateFormats.DAY) : '';
    }

    const formatCreationDate = (): string => {
        return creationDate ? moment(new Date(creationDate)).format(DateFormats.MONTH_YEAR) : '';
    }

    const formatType = (): string => {
        return subscriptionTypeLabels[type] ? subscriptionTypeLabels[type] : type.replaceAll('_', ' ');
    }

    return {
        type: formatType(),
        amount: formatAmount(),
        serviceMonth: formatCreationDate(),
        invoiceNumber: transactionNumber,
        paymentDate: formatDate(),
        status: formatStatus()
    };
}

export const formatSubscriptionItemsForCSV = (subscriptionItems: FormattedSubscriptionItem[]): SubscriptionCsvItem[] => {

    return subscriptionItems.map((item) => {
        const {feeType, amount, createdOn, createdBy, paymentSource, active, reason} = item;

        return ({
            feeType: feeType,
            amount: amount,
            createdOn: createdOn,
            createdBy: createdBy,
            paymentSource: paymentSource,
            active: active,
            reason: reason
        });
    })
};
